import type ExcelJS from "exceljs";
import {
  normalizeCatalogName,
  productCategoryIds,
  productCategoryLabels,
  type CatalogProductBrandLink,
} from "./catalog-data";

const MAX_IMPORT_ROWS = 5_000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function downloadBuffer(buffer: ExcelJS.Buffer, filename: string) {
  const blob = new Blob([buffer as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function styleWorksheet(worksheet: ExcelJS.Worksheet) {
  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  worksheet.autoFilter = { from: "A1", to: `${worksheet.getColumn(worksheet.columnCount).letter}1` };
  const header = worksheet.getRow(1);
  header.height = 24;
  header.font = { bold: true, color: { argb: "FFFFFFFF" } };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF16324F" } };
  header.alignment = { vertical: "middle" };
  worksheet.eachRow((row) => { row.alignment = { vertical: "middle" }; });
}

function addCatalogueColumns(worksheet: ExcelJS.Worksheet) {
  worksheet.columns = [
    { header: "Product", key: "productName", width: 30 },
    { header: "Brand", key: "brandName", width: 28 },
    { header: "Brand Detail", key: "brandDetail", width: 58 },
    { header: "Source URL", key: "sourceUrl", width: 62 },
    { header: "Product Image", key: "imageUrl", width: 72 },
  ];
}

export async function downloadCatalogTemplate() {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Kitanego";
  workbook.created = new Date();
  workbook.subject = "Linked product and brand catalogue import template";
  const worksheet = workbook.addWorksheet("Product Catalogue");
  addCatalogueColumns(worksheet);
  styleWorksheet(worksheet);

  const instructions = workbook.addWorksheet("Instructions");
  instructions.columns = [{ width: 105 }];
  instructions.addRows([
    ["Enter one linked product and brand record per row on the Product Catalogue sheet."],
    ["Product and Brand are required. Brand Detail, Source URL and Product Image are retained in the database and backup, but are not displayed to members."],
    ["Keep the headings Product, Brand, Brand Detail, Source URL and Product Image unchanged."],
    ["The same Product and Brand can appear on multiple rows when each row has different Brand Detail information."],
    ["Uploads add new records and update matching records. Empty rows and exact duplicate rows are ignored."],
  ]);
  instructions.getRow(1).font = { bold: true };
  instructions.eachRow((row) => { row.alignment = { wrapText: true, vertical: "top" }; });

  downloadBuffer(await workbook.xlsx.writeBuffer(), "kitanego-product-brand-template.xlsx");
}

export async function downloadCatalogBackup(entries: CatalogProductBrandLink[]) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Kitanego";
  workbook.created = new Date();
  workbook.subject = "Linked product and brand catalogue backup";

  const catalogueSheet = workbook.addWorksheet("Product Catalogue");
  addCatalogueColumns(catalogueSheet);
  catalogueSheet.addRows(entries
    .slice()
    .sort((left, right) => left.productName.localeCompare(right.productName, undefined, { sensitivity: "base", numeric: true })
      || left.brandName.localeCompare(right.brandName, undefined, { sensitivity: "base", numeric: true })
      || left.brandDetail.localeCompare(right.brandDetail, undefined, { sensitivity: "base", numeric: true }))
    .map((entry) => ({
      productName: entry.productName,
      brandName: entry.brandName,
      brandDetail: entry.brandDetail,
      sourceUrl: entry.sourceUrl,
      imageUrl: entry.imageUrl,
    })));
  styleWorksheet(catalogueSheet);

  const categorySheet = workbook.addWorksheet("App Categories");
  categorySheet.columns = [
    { header: "Category ID", key: "id", width: 20 },
    { header: "Category Name", key: "name", width: 28 },
  ];
  categorySheet.addRows(productCategoryIds.map((category) => ({ id: category, name: productCategoryLabels[category] })));
  styleWorksheet(categorySheet);

  const productCount = new Set(entries.map((entry) => entry.productName.toLocaleLowerCase())).size;
  const brandCount = new Set(entries.map((entry) => entry.brandName.toLocaleLowerCase())).size;
  const summarySheet = workbook.addWorksheet("Backup Summary");
  summarySheet.columns = [{ width: 28 }, { width: 36 }];
  summarySheet.addRows([
    ["Backup created", new Intl.DateTimeFormat("en-MY", { dateStyle: "medium", timeStyle: "medium", timeZone: "Asia/Kuching" }).format(new Date())],
    ["Linked catalogue rows", entries.length],
    ["Products", productCount],
    ["Brands", brandCount],
    ["Brand Detail included", "Yes — stored for future use"],
    ["Source", "Kitanego Product and Brand settings"],
  ]);
  summarySheet.getColumn(1).font = { bold: true };
  summarySheet.eachRow((row) => { row.alignment = { vertical: "middle" }; });

  const date = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuching" }).format(new Date());
  downloadBuffer(await workbook.xlsx.writeBuffer(), `kitanego-product-brand-backup-${date}.xlsx`);
}

function cellText(cell: ExcelJS.Cell, maxLength = 240) {
  const value = cell.value;
  if (value == null) return "";
  if (typeof value === "object" && "text" in value) return normalizeCatalogName(String(value.text), maxLength);
  if (typeof value === "object" && "result" in value) return normalizeCatalogName(String(value.result ?? ""), maxLength);
  return normalizeCatalogName(String(value), maxLength);
}

async function readWorkbook(file: File) {
  if (file.size > MAX_FILE_SIZE) throw new Error("Excel files must be 10 MB or smaller.");
  if (!file.name.toLocaleLowerCase().endsWith(".xlsx")) throw new Error("Upload an .xlsx catalogue file.");
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  return workbook.getWorksheet("Product Catalogue") ?? workbook.worksheets[0];
}

export async function parseCatalogTemplate(file: File): Promise<CatalogProductBrandLink[]> {
  const worksheet = await readWorkbook(file);
  if (!worksheet) throw new Error("The workbook does not contain a Product Catalogue sheet.");
  const headers = [cellText(worksheet.getCell("A1")), cellText(worksheet.getCell("B1")), cellText(worksheet.getCell("C1"))]
    .map((header) => header.toLocaleLowerCase());
  if (headers[0] !== "product" || headers[1] !== "brand" || headers[2] !== "brand detail") {
    throw new Error("Use the catalogue template without changing the Product, Brand and Brand Detail headings.");
  }

  const entries: CatalogProductBrandLink[] = [];
  const errors: string[] = [];
  const lastRow = Math.min(worksheet.rowCount, MAX_IMPORT_ROWS + 1);
  for (let rowNumber = 2; rowNumber <= lastRow; rowNumber += 1) {
    const productName = cellText(worksheet.getCell(rowNumber, 1), 80);
    const brandName = cellText(worksheet.getCell(rowNumber, 2), 60);
    const brandDetail = cellText(worksheet.getCell(rowNumber, 3), 240);
    const sourceUrl = cellText(worksheet.getCell(rowNumber, 4), 1_000);
    const imageUrl = cellText(worksheet.getCell(rowNumber, 5), 1_000);
    if (!productName && !brandName && !brandDetail && !sourceUrl && !imageUrl) continue;
    if (!productName) errors.push(`row ${rowNumber}: Product is required`);
    if (!brandName) errors.push(`row ${rowNumber}: Brand is required`);
    if (productName && brandName) entries.push({ productName, brandName, brandDetail, sourceUrl, imageUrl });
  }
  if (worksheet.rowCount > MAX_IMPORT_ROWS + 1) errors.push(`only the first ${MAX_IMPORT_ROWS} data rows can be imported`);
  if (errors.length) throw new Error(`Fix the spreadsheet: ${errors.slice(0, 5).join("; ")}${errors.length > 5 ? `; and ${errors.length - 5} more` : ""}.`);
  if (!entries.length) throw new Error("Add at least one linked product and brand row before uploading the catalogue.");
  return Array.from(new Map(entries.map((entry) => [
    [entry.productName, entry.brandName, entry.brandDetail].map((value) => value.toLocaleLowerCase()).join("::"),
    entry,
  ])).values());
}
